import { useMutation, useQueryClient } from '@tanstack/react-query';

import { CALENDAR_VISIBILITY_TOOGLES_IDENTIFIER } from '@/app/(main)/calendar/consts/calendar-user-settings';
import { getUserSettingsQueryKey } from '@/consts/user-settings';
import {
  mapCalendarPreferencesToUserSettingsApi,
  unwrapCalendarUserSettingsResponse,
} from '@/app/(main)/calendar/utils/mapCalendarUserSettings';
import { useFetch } from '@/hooks/useFetch';
import type { CalendarPreferences } from '@/types/calendarTypes';
import type {
  PostCalendarUserSettingsRequest,
  PostCalendarUserSettingsResponse,
} from '@/types/calendarUserSettingsApiTypes';

type MutationContext = {
  previous: CalendarPreferences | undefined;
};

export function useUpdateCalendarPreferencesMutation() {
  const queryClient = useQueryClient();
  const { doFetch } = useFetch<PostCalendarUserSettingsRequest, PostCalendarUserSettingsResponse>();

  return useMutation<CalendarPreferences, Error, CalendarPreferences, MutationContext>({
    mutationFn: async (preferences) => {
      const response = await doFetch('/api/user/settings', {
        method: 'POST',
        body: {
          identifier: CALENDAR_VISIBILITY_TOOGLES_IDENTIFIER,
          settings: mapCalendarPreferencesToUserSettingsApi(preferences),
        },
      });

      return unwrapCalendarUserSettingsResponse(response.data);
    },
    onMutate: async (preferences) => {
      const queryKey = getUserSettingsQueryKey(CALENDAR_VISIBILITY_TOOGLES_IDENTIFIER);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<CalendarPreferences>(queryKey);
      queryClient.setQueryData<CalendarPreferences>(queryKey, preferences);
      return { previous };
    },
    onError: (_error, _preferences, context) => {
      const queryKey = getUserSettingsQueryKey(CALENDAR_VISIBILITY_TOOGLES_IDENTIFIER);
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSuccess: (preferences) => {
      queryClient.setQueryData(getUserSettingsQueryKey(CALENDAR_VISIBILITY_TOOGLES_IDENTIFIER), preferences);
    },
  });
}
